import { useCallback, useRef } from "react"
import { isEqual, toLower } from "lodash"
import { useDispatch } from "react-redux"
import { useAccountAlias } from "resources/account"
import { ASSET_TYPES_MAP } from "helpers/assets/constants"
import { Creators as ApiCreators } from "model/apiCall"
import { Creators as RecentInstrumentsCreators } from "../../reducers/app/recentInstruments"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"

const RESOURCE_TYPES = [ASSET_TYPES_MAP.PORTFOLIO, ASSET_TYPES_MAP.SWAP, ASSET_TYPES_MAP.BASKET]

function isInstrumentAResource(instrument) {
  return RESOURCE_TYPES.includes(instrument.assetClass || instrument.type)
}

export function getIdAndTypeFromInstrument(instrument) {
  const { id, assetClass, type, assetId, assetType, assetTickerMic, security, resourceId, internalId, ticker, mic, instrumentType, instrumentId } = instrument
  if (instrumentType && instrumentId) {
    return { instrumentId, instrumentType }
  }

  const shouldUseResourceId = !id || (resourceId && assetClass && toLower(assetClass) === "swap")

  if (shouldUseResourceId) {
    return { instrumentId: resourceId, instrumentType: toLower(assetClass) || type || assetType }
  }

  if (security || assetClass === "equity" || !!internalId) {
    return { instrumentId: `${internalId}.${ticker}.${mic}`, instrumentType: "asset" }
  }

  if (id && isInstrumentAResource(instrument)) {
    return { instrumentId: id, instrumentType: assetClass }
  }

  if (type === "index" || assetType === "index") {
    return { instrumentId: id, instrumentType: "index" }
  }

  if (assetId && assetType === "index") {
    return { instrumentId: assetId, instrumentType: "index" }
  }

  if (assetType && assetTickerMic) {
    const { ticker, mic } = assetTickerMic
    return { instrumentId: `${assetId}.${ticker}.${mic}`, instrumentType: assetType }
  }

  if (id) {
    return { instrumentId: id, instrumentType: toLower(type) }
  }

  return {}
}

export function useSaveRecentInstrument(modelId) {
  const dispatch = useDispatch()
  const accountAlias = useAccountAlias()
  const lastInstrument = useRef(null)

  const saveRecentInstrument = useCallback((instrument, searchType) => {
    const { instrumentId, instrumentType } = getIdAndTypeFromInstrument(instrument)

    if (!instrumentId || !modelId || !accountAlias) return

    const body = {
      instrumentId,
      instrumentType: toLower(instrumentType),
      searchType: searchType || RECENT_INSTRUMENT_SEARCH_TYPES.GENERAL,
    }

    // We don't want to trigger the API call if the requests are coming first from selecting an instrument from the unified search
    // And then immediately after from loading the instrument modal or page
    const isDuplicate = isEqual(lastInstrument?.current, body)

    if (isDuplicate) return
    lastInstrument.current = body

    dispatch(ApiCreators.callEndpoint(
      `/api/${accountAlias}/recent-instruments?${new URLSearchParams({ modelId })}`,
      "PUT",
      body,
      returnedInstrument => {
        dispatch(RecentInstrumentsCreators.updateRecentInstrument({ type: instrument.type || instrumentType, searchType, ...returnedInstrument }))
      },
    ))
  }, [dispatch, accountAlias, modelId, lastInstrument])

  return { saveRecentInstrument }
}
